import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  getLanguageMeta,
  isSupportedLanguage,
  LANGUAGE_STORAGE_KEY,
  PUBLIC_LANGUAGES,
} from "../utils/languages";

const LanguageContext = createContext(null);

const readStoredLanguage = () => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return isSupportedLanguage(stored) ? stored : "en";
};

const isAdminShellPath = (pathname) =>
  pathname.startsWith("/dashboard") || pathname.startsWith("/login");

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readStoredLanguage);
  const location = useLocation();

  const setLanguage = useCallback((code) => {
    if (!isSupportedLanguage(code)) return;
    setLanguageState(code);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  }, []);

  useEffect(() => {
    const meta = getLanguageMeta(language);
    document.documentElement.lang = meta.code;
    // Admin UI stays LTR even when public language is AR/KU.
    document.documentElement.dir = isAdminShellPath(location.pathname)
      ? "ltr"
      : meta.dir;
  }, [language, location.pathname]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      languages: PUBLIC_LANGUAGES,
      meta: getLanguageMeta(language),
    }),
    [language, setLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return ctx;
};
